import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import type { FC } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import Banner from '@/components/UI/Banner';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { MetadataItem } from '@/model/Metadata.model';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { containsNoSpaces, containsValidCharacters, withinCharacterLimits } from '@/validators';

import { getMetadataFields } from '../AddEditLink/AddEditLink.utils';

type MetadataFormValues = Record<string, unknown>;

interface AddFileMetaFormData {
  filename: string;
  metadata: MetadataFormValues;
}

interface AddFileMetaProps {
  handleSave: (data: { filename: string; metadata?: MetadataFormValues }) => void;
  closeModal: () => void;
  metadata: Array<MetadataItem>;
  filename: string;
  isEdit: boolean;
  files: Array<{ name: string }>;
}

const AddFileMeta: FC<AddFileMetaProps> = ({ handleSave, closeModal, metadata, filename, isEdit, files }) => {
  const formMethods = useForm<AddFileMetaFormData>({
    defaultValues: {
      filename: filename?.replace(/ /g, '_') ?? '',
      metadata: {},
    },
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = formMethods;

  const fileName = useWatch({ control, name: 'filename' });
  const fileExists = _notNil(files.find(({ name }) => name === fileName));

  return (
    <ModalContainer size="narrow" className="bg-white f6 near-black ">
      <ModalHeader
        title={isEdit ? 'Edit file' : 'Add a file'}
        closeModal={closeModal}
        closeDisabled={false}
        className="pa3 bb b--moon-gray w-100"
      />
      <FormProvider {...formMethods}>
        <form className="pt3">
          {!isEdit && fileExists && (
            <Banner info className="mh3 mb3" dismiss={false} center>
              <h3 className="f6 normal lh-title">
                A file with this name already exists - continuing will overwrite it.
              </h3>
            </Banner>
          )}
          <div className="pb3 ph3" style={{ whiteSpace: 'break-spaces' }}>
            <label htmlFor="filename">File Name</label>
            <input
              id="filename"
              type="text"
              data-testid="filename-input"
              style={{ marginBottom: 0 }}
              className={cn({
                input__error: _notNil(errors.filename),
                'disabled-link': isEdit,
              })}
              {...register('filename', {
                required: 'Required',
                validate: {
                  containsNoSpaces,
                  withinCharacterLimits: (value: string) => withinCharacterLimits(value, 3, 500),
                  containsValidCharacters: (value: string) =>
                    containsValidCharacters(
                      value,
                      "File name can only contain letters, numbers or some special characters: _ - . ! * ' ( )"
                    ),
                },
              })}
            />
            <ErrorMessage
              errors={errors}
              name="filename"
              render={({ message }) => <p className="f6 red db pt2">{message}</p>}
            />
          </div>
          {_isNotEmpty(metadata) && <div className="pb3 ph3">{getMetadataFields(metadata, {})}</div>}
        </form>
      </FormProvider>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onCancel={closeModal}
        cancelBtnText="Close"
        submitBtnText={fileExists ? 'Overwrite' : 'Save'}
        submitButtonProps={{
          className: 'ml3',
          disabled: _isNotEmpty(errors),
        }}
        onSubmit={handleSubmit((data) => handleSave(data))}
      />
    </ModalContainer>
  );
};

export default AddFileMeta;
